<script>
  import { createEventDispatcher } from "svelte";
  import { Modal, ModalBody, ModalFooter, ModalHeader } from "sveltestrap";
  import alertify from "alertifyjs";
  import backend from "../../../../../../server";
  import { ICONS } from "../../../../../../js/icons";
  import PlayerManagement from "../../PlayerManagement.svelte";
  import { computeCampaignStatus, isCampaignImmutable } from "../../../../../utils/statusEngine.js";

  export let show = false;
  export let campaign = null;
  export let getCampaignById = async () => null;
  export let demoConfig = null;

  const dispatch = createEventDispatcher();
  let showPlayerManagement = false;
  let showAssignmentForm = false;
  let pendingPlayers = [];
  let requestId = "";
  let config = emptyConfig();
  let busy = false;
  let errorMessage = "";
  let queriedBonuses = new Map();
  let queryingPlayers = new Set();

  function emptyConfig() {
    return {
      expirationDate: "",
      validityDate: "",
      chipsNumber: "",
      maxBetLimit: "",
      maxWinLimitPlayerCur: "",
    };
  }

  const clean = (value) => String(value ?? "").trim();
  const responseData = (response) => response?.data ?? response ?? {};
  function errorDescription(error, fallback) {
    const description = error?.response?.data?.description ?? error?.response?.data?.error ?? error?.message;
    if (!description) return fallback;
    const message = String(description);
    const dateError = /(date|fecha|expiration|expiry|validity|start).*(future|current|now|after|greater|later|expired|past)|must be.*(future|after)|should be.*(future|after)/i;
    if (dateError.test(message)) {
      return "La fecha y hora deben ser posteriores a la fecha y hora actual.";
    }
    const technicalDetails = /(https?:\/\/|java\.|exception|sockettimeout|i\/o error|read timed out|post request)/i;
    return technicalDetails.test(message) ? fallback : message;
  }

  function minimumFutureDateTime() {
    const date = new Date(Date.now() + 60000);
    date.setSeconds(0, 0);
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }
  const playerId = (player) => clean(player?.playerId ?? player?.id);
  const playerName = (player) => clean(player?.playerName ?? player?.username_origin ?? player?.username ?? playerId(player));

  $: clientCode = clean(campaign?.client?.clientCode ?? campaign?.client?.code);
  $: clientId = clean(campaign?.client?.clientId ?? campaign?.client?.id);
  $: bonusCode = clean(campaign?.campaignId ?? campaign?.bonusCode);
  $: players = Array.isArray(campaign?.playerList) ? campaign.playerList : [];
  $: assignedIds = players.map(playerId).filter(Boolean);
  $: campaignStartMs = campaign?.startDate ? new Date(campaign.startDate).getTime() : 0;
  $: campaignEndMs = campaign?.endDate ? new Date(campaign.endDate).getTime() : 0;
  $: baseCampaignStatus = Number(campaign?.baseStatus ?? campaign?.campaignStatus ?? campaign?.statusCode ?? 4);
  $: effectiveCampaignStatus = computeCampaignStatus(baseCampaignStatus, campaignStartMs, campaignEndMs);
  $: campaignIsClosed = isCampaignImmutable(effectiveCampaignStatus);
  $: isDemo = !!demoConfig && (
    clientCode === clean(demoConfig?.CODE) || clientId === clean(demoConfig?.ID)
  );
  $: selectorStaticPlayers = isDemo && Array.isArray(demoConfig?.PLAYERS)
    ? demoConfig.PLAYERS.map((player) => ({
        value: clean(player?.id),
        label: clean(player?.label ?? player?.username ?? player?.id),
        username: clean(player?.username),
        currency: clean(player?.currency ?? campaign?.currency),
        country: clean(player?.country),
      })).filter((player) => player.value)
    : null;

  function close() {
    showPlayerManagement = false;
    showAssignmentForm = false;
    pendingPlayers = [];
    requestId = "";
    config = emptyConfig();
    errorMessage = "";
    show = false;
    dispatch("close");
  }

  function openPlayerSelector() {
    errorMessage = "";
    if (campaignIsClosed) {
      errorMessage = "La campaña está finalizada o eliminada y no permite modificar jugadores.";
      return;
    }
    showPlayerManagement = true;
  }

  function handlePlayersSelected(event) {
    const selected = Array.isArray(event.detail?.players) ? event.detail.players : [];
    showPlayerManagement = false;
    errorMessage = "";
    if (!selected.length) {
      errorMessage = "Selecciona al menos un jugador.";
      return;
    }
    pendingPlayers = selected.map((player) => ({
      playerId: clean(player?.id),
      playerName: clean(player?.label ?? player?.username_origin ?? player?.username ?? player?.id),
    })).filter((player) => player.playerId && !assignedIds.includes(player.playerId));
    if (!pendingPlayers.length) {
      errorMessage = "Los jugadores seleccionados ya están asignados a la campaña.";
      return;
    }
    requestId = "";
    config = emptyConfig();
    showAssignmentForm = true;
  }

  function returnToPlayerSelector() {
    errorMessage = "";
    showPlayerManagement = true;
  }

  function cancelAssignment() {
    showAssignmentForm = false;
    pendingPlayers = [];
    requestId = "";
    config = emptyConfig();
    errorMessage = "";
  }

  async function refreshCampaign() {
    if (!bonusCode) return;
    const response = await getCampaignById(bonusCode);
    const raw = response?.data ?? response;
    const fresh = Array.isArray(raw) ? raw[0] : raw;
    if (fresh) {
      campaign = { ...campaign, ...fresh };
      dispatch("updated", campaign);
    }
  }

  function assertSuccess(response) {
    const result = responseData(response);
    if (String(result?.error) !== "0") {
      throw new Error(result?.description || result?.error || "Error del proveedor");
    }
    return result;
  }

  function epochSeconds(value) {
    if (!value) return undefined;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? undefined : Math.floor(timestamp / 1000);
  }

  function positiveNumber(value, integer = false) {
    if (value === "" || value === null || value === undefined) return undefined;
    const number = Number(value);
    if (!(number > 0) || (integer && !Number.isInteger(number))) return null;
    return number;
  }

  function buildOverrideConfig() {
    const expirationDate = epochSeconds(config.expirationDate);
    const validityDate = epochSeconds(config.validityDate);
    if ((config.expirationDate && expirationDate === undefined) || (config.validityDate && validityDate === undefined)) {
      throw new Error("Las fechas de sobrescritura no son válidas");
    }
    const now = Math.floor(Date.now() / 1000);
    if (expirationDate !== undefined && expirationDate <= now) {
      throw new Error("La fecha y hora de expiración deben ser posteriores a la fecha y hora actual");
    }
    if (validityDate !== undefined && validityDate <= now) {
      throw new Error("La fecha y hora de validez deben ser posteriores a la fecha y hora actual");
    }
    if (expirationDate && validityDate && validityDate < expirationDate) {
      throw new Error("La fecha de validez debe ser posterior a la expiración");
    }
    const chipsNumber = positiveNumber(config.chipsNumber, true);
    const maxBetLimit = positiveNumber(config.maxBetLimit, true);
    const maxWinLimitPlayerCur = positiveNumber(config.maxWinLimitPlayerCur);
    if ([chipsNumber, maxBetLimit, maxWinLimitPlayerCur].includes(null)) {
      throw new Error("Las cantidades de sobrescritura deben ser mayores que cero");
    }
    const overrideConfig = Object.fromEntries(Object.entries({
      expirationDate,
      validityDate,
      chipsNumber,
      maxBetLimit,
      maxWinLimitPlayerCur,
    }).filter(([, value]) => value !== undefined));
    return Object.keys(overrideConfig).length ? overrideConfig : null;
  }

  async function addSelectedPlayers() {
    if (!pendingPlayers.length) return;
    if (campaignIsClosed) {
      errorMessage = "La campaña está finalizada o eliminada y no permite modificar jugadores.";
      return;
    }
    const playersToAdd = pendingPlayers;
    const playerCount = playersToAdd.length;
    busy = true;
    errorMessage = "";
    try {
      const commonParams = {
        clientCode,
        bonusCode,
        ...(clean(requestId) ? { requestId: clean(requestId) } : {}),
      };
      if (playerCount === 1) {
        const selectedPlayer = playersToAdd[0];
        assertSuccess(await backend.freespin.addFreeChipPlayerPrg(
          { ...commonParams, ...selectedPlayer },
          { config: buildOverrideConfig() },
        ));
      } else {
        assertSuccess(await backend.freespin.addFreeChipPlayersPrg(
          commonParams,
          { playerList: playersToAdd },
        ));
      }
      showAssignmentForm = false;
      pendingPlayers = [];
      requestId = "";
      config = emptyConfig();
      await refreshCampaign();
      alertify.success(
        playerCount === 1
          ? "Free Chips asignados al jugador"
          : "Jugadores agregados a Free Chips",
      );
    } catch (error) {
      errorMessage = errorDescription(error, "No se pudieron asignar los Free Chips");
    } finally {
      busy = false;
    }
  }

  async function removePlayer(player) {
    const id = playerId(player);
    if (campaignIsClosed) {
      errorMessage = "La campaña está finalizada o eliminada y no permite modificar jugadores.";
      return;
    }
    if (!id || !confirm(`¿Remover al jugador ${playerName(player)} de la campaña?`)) return;
    busy = true;
    errorMessage = "";
    try {
      assertSuccess(await backend.freespin.removeFreeChipPlayersPrg(
        { clientCode, bonusCode, ...(clean(requestId) ? { requestId: clean(requestId) } : {}) },
        { playerList: [id] },
      ));
      queriedBonuses.delete(id);
      queriedBonuses = new Map(queriedBonuses);
      await refreshCampaign();
      alertify.success("Jugador removido de Free Chips");
    } catch (error) {
      errorMessage = errorDescription(error, "No se pudo remover el jugador");
    } finally {
      busy = false;
    }
  }

  async function queryPlayer(player) {
    const id = playerId(player);
    if (!id || queryingPlayers.has(id)) return;
    queryingPlayers.add(id);
    queryingPlayers = new Set(queryingPlayers);
    errorMessage = "";
    try {
      const result = responseData(await backend.freespin.getFreeChipsByPlayerPrg({
        clientCode,
        playerId: id,
        options: "inPlayerCurrency",
      }));
      if (String(result?.error) !== "0") throw new Error(result?.description || result?.error);
      queriedBonuses.set(id, Array.isArray(result?.bonuses) ? result.bonuses : []);
      queriedBonuses = new Map(queriedBonuses);
    } catch (error) {
      errorMessage = errorDescription(error, "No se pudieron consultar los Free Chips del jugador");
    } finally {
      queryingPlayers.delete(id);
      queryingPlayers = new Set(queryingPlayers);
    }
  }
</script>

<Modal isOpen={show} toggle={close} size="xl" centered={true} modalClassName="pragmatic-free-chip-players-modal">
  <ModalHeader toggle={close}>{campaign?.campaignName || "Jugadores Free Chips"}</ModalHeader>
  <ModalBody>
    <div class="top-actions">
      <div class="small text-muted">
        Cliente: {campaign?.client?.clientName || "—"} · Moneda: {campaign?.currency || "—"}
      </div>
      {#if showAssignmentForm}
        <span class="step-indicator">Paso 2 de 2</span>
      {:else}
        <button type="button" class="btn btn-primary btn-sm" disabled={busy || !clientId || campaignIsClosed} on:click={openPlayerSelector}>
          {@html ICONS.addCircle} Agregar jugadores
        </button>
      {/if}
    </div>

    {#if errorMessage}<div class="alert alert-danger" role="alert">{errorMessage}</div>{/if}
    {#if campaignIsClosed}
      <div class="alert alert-warning read-only-message" role="status">
        Campaña finalizada o eliminada. La asignación de jugadores está disponible solo para consulta.
      </div>
    {/if}

    {#if showAssignmentForm}
      <section class="section-box assignment-panel">
        <div class="assignment-step-header">
          <button
            type="button"
            class="back-button"
            title="Volver a seleccionar jugadores"
            aria-label="Volver a seleccionar jugadores"
            disabled={busy}
            on:click={returnToPlayerSelector}
          >
            {@html ICONS.left}
          </button>
          <h6>Configurar asignación</h6>
        </div>
        <p class="selected-summary">Jugadores seleccionados: <strong>{pendingPlayers.length}</strong></p>
        <div class="request-field">
          <label class="field">
            <span>Request ID <small>(opcional)</small></span>
            <input class="form-control" bind:value={requestId} placeholder="Identificador opcional de la solicitud" disabled={busy} />
          </label>
        </div>

        {#if pendingPlayers.length === 1}
          <h6 class="config-title">Configuración de jugador <small>(opcional)</small></h6>
          <div class="override-grid">
            <label class="field"><span>Expiración</span><input class="form-control" type="datetime-local" min={minimumFutureDateTime()} bind:value={config.expirationDate} disabled={busy} /></label>
            <label class="field"><span>Validez</span><input class="form-control" type="datetime-local" min={minimumFutureDateTime()} bind:value={config.validityDate} disabled={busy} /></label>
            <label class="field"><span>Cantidad de chips</span><input class="form-control" type="number" min="1" step="1" bind:value={config.chipsNumber} disabled={busy} /></label>
            <label class="field"><span>Máximo por ronda</span><input class="form-control" type="number" min="1" step="1" bind:value={config.maxBetLimit} disabled={busy} /></label>
            <label class="field"><span>Máximo de ganancia</span><input class="form-control" type="number" min="0.000001" step="any" bind:value={config.maxWinLimitPlayerCur} disabled={busy} /></label>
          </div>
        {/if}
        <p class="selected-summary">Si no se ingresa ningun campo, la configuración será tomada directamnte de lo registrado al crear la campaña. </p>
      </section>
    {:else}
      <section class="section-box">
      <h6>Jugadores asignados ({players.length})</h6>
      {#if players.length === 0}
        <p class="state-message">La campaña todavía no tiene jugadores.</p>
      {:else}
        <div class="table-responsive">
          <table class="table table-sm table-striped table-bordered align-middle">
            <thead><tr><th>N°</th><th>ID</th><th>Jugador</th><th>Estado</th><th>Acciones</th></tr></thead>
            <tbody>
              {#each players as player, index (playerId(player))}
                <tr>
                  <td>{index + 1}</td>
                  <td>{playerId(player)}</td>
                  <td>{playerName(player)}</td>
                  <td>{Number(player?.playerStatus) === 0 ? "Eliminado" : "Asignado"}</td>
                  <td class="actions-cell">
                    <button type="button" class="btn btn-outline-primary btn-sm" disabled={queryingPlayers.has(playerId(player))} on:click={() => queryPlayer(player)}>
                      {queryingPlayers.has(playerId(player)) ? "Consultando…" : "Consultar"}
                    </button>
                    <button type="button" class="btn btn-outline-danger btn-sm" disabled={busy || campaignIsClosed} on:click={() => removePlayer(player)}>Remover</button>
                  </td>
                </tr>
                {#if queriedBonuses.has(playerId(player))}
                  {@const bonuses = queriedBonuses.get(playerId(player)) || []}
                  <tr class="bonus-detail-row">
                    <td colspan="5">
                      {#if bonuses.length === 0}
                        <span class="text-muted">Pragmatic no devolvió Free Chips activos para este jugador.</span>
                      {:else}
                        <div class="bonus-cards">
                          {#each bonuses as bonus}
                            <article class="bonus-card">
                              <strong>{bonus.bonusCode}</strong>
                              <span>{bonus.currency || "—"} {bonus.chipValue}</span>
                              <span>Chips: {bonus.chipsNumberPlayed}/{bonus.chipsNumber}</span>
                              <span>Juegos: {bonus.gameIDList || "—"}</span>
                              <span>Expira: {bonus.expirationDate || "—"}</span>
                            </article>
                          {/each}
                        </div>
                      {/if}
                    </td>
                  </tr>
                {/if}
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
      </section>
    {/if}
  </ModalBody>
  <ModalFooter>
    {#if showAssignmentForm}
      <button type="button" class="btn btn-secondary" disabled={busy} on:click={cancelAssignment}>Cancelar</button>
      <button type="button" class="btn btn-primary" disabled={busy || !pendingPlayers.length || campaignIsClosed} on:click={addSelectedPlayers}>
        {busy ? "Asignando…" : "Asignar Free Chips"}
      </button>
    {:else}
      <button type="button" class="btn btn-secondary" disabled={busy} on:click={close}>Cerrar</button>
    {/if}
  </ModalFooter>
</Modal>

{#if showPlayerManagement}
  <div class="nested-dim" aria-hidden="true"></div>
  <PlayerManagement
    {clientId}
    currency={campaign?.currency || ""}
    initialSelected={pendingPlayers.map((player) => player.playerId)}
    idStrategy="auto"
    excludeIds={assignedIds}
    staticPlayers={selectorStaticPlayers}
    confirmLabel="Continuar"
    stepLabel="Paso 1 de 2"
    on:close={() => (showPlayerManagement = false)}
    on:confirm={handlePlayersSelected}
  />
{/if}

<style>
  :global(.pragmatic-free-chip-players-modal .modal-dialog) { width: calc(100% - 2rem); max-width: 900px; }
  :global(.pragmatic-free-chip-players-modal .modal-content) { height: 80vh; }
  :global(.pragmatic-free-chip-players-modal .modal-body) { overflow: auto; }
  .section-box { margin-bottom: 1rem; padding: 0.85rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; background: #fafbfc; }
  .section-box h6 { margin: 0 0 0.75rem; color: #333; font-weight: 700; }
  .top-actions { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 0.75rem; }
  .read-only-message { margin-bottom: 0.75rem; }
  .nested-dim { position: fixed; inset: 0; z-index: 1059; background: rgba(0, 0, 0, 0.45); }
  .assignment-panel { min-height: 16rem; }
  .assignment-step-header { display: flex; align-items: center; gap: 0.65rem; margin-bottom: 0.75rem; }
  .assignment-step-header h6 { margin: 0; }
  .step-indicator { display: inline-flex; align-items: center; padding: 0.25rem 0.55rem; border-radius: 999px; background: #e7f1ff; color: #0d6efd; font-size: 0.75rem; font-weight: 700; white-space: nowrap; }
  .back-button { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; padding: 0; border: 1px solid #ced4da; border-radius: 8px; background: #fff; color: #495057; }
  .back-button:hover:not(:disabled) { border-color: #0d6efd; background: #f0f6ff; color: #0d6efd; }
  .back-button:disabled { cursor: not-allowed; opacity: 0.55; }
  .selected-summary { margin: 0 0 0.75rem; color: #555; font-size: 0.9rem; }
  .request-field { max-width: 430px; }
  .section-box .config-title { margin-top: 1rem; }
  .section-box .config-title small { color: #6c757d; font-size: 0.75rem; font-weight: 400; }
  .override-grid { display: grid; grid-template-columns: repeat(3, minmax(140px, 1fr)); gap: 0.6rem; margin-top: 0.75rem; }
  .field { display: flex; flex-direction: column; gap: 0.3rem; color: #555; font-size: 0.75rem; font-weight: 600; }
  .field input { min-height: 38px; font-size: 0.875rem; }
  .actions-cell { display: flex; gap: 0.4rem; }
  .bonus-detail-row td { padding: 0.75rem; background: #f4f7ff; }
  .bonus-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 0.5rem; }
  .bonus-card { display: flex; flex-direction: column; gap: 0.2rem; padding: 0.6rem; border: 1px solid #cfe2ff; border-radius: 0.4rem; background: #fff; font-size: 0.8rem; }
  .state-message { margin: 0; color: #6c757d; text-align: center; }
  @media (max-width: 720px) {
    :global(.pragmatic-free-chip-players-modal .modal-dialog) { width: calc(100% - 1rem); margin: 0.5rem; }
    :global(.pragmatic-free-chip-players-modal .modal-content) { height: calc(100vh - 1rem); }
    .top-actions { align-items: flex-start; flex-direction: column; }
    .override-grid { grid-template-columns: 1fr; }
  }
</style>
